import { Injectable } from '@angular/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { NGXLogger } from 'ngx-logger';
import {Md5} from 'ts-md5';
import { HttpClient } from '@angular/common/http';
import { from, isObservable, Observable, Subject } from 'rxjs';
import { concatMap, filter, map, mergeMap, toArray } from 'rxjs/operators';
import { Capacitor, CapacitorException } from '@capacitor/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

export interface DataTransform<T> {
  url: string;
  data: T;
}

@Injectable({
  providedIn: 'root'
})
export class MediaCacheService {

  private cachedUris: Map<string, SafeUrl | Observable<SafeUrl>> = new Map<string, SafeUrl | Observable<SafeUrl>>();

  private readonly cachePath = 'mediacache';
  private readonly directory = Directory.Data;

  constructor(
    private logger: NGXLogger,
    private http: HttpClient,
    private sanitizer: DomSanitizer
    ) { }

  async clear() {

    try{
      await Filesystem.rmdir({
        path: this.cachePath,
        directory: this.directory,
        recursive: true
      });

    }catch (e){
      this.logger.warn('Could not delete cache directory!', e);
    }

    // this.cachedUris.forEach((k,v) =>{
    //   URL.revokeObjectURL(v);
    // });

    this.cachedUris.clear();

    this.logger.info('Media Cache cleared.');
  }

   async cacheFiles(urls: string[]): Promise<any> {

    this.logger.log('Caching files...', urls);

    let dir = [];


    try {
      const res = await Filesystem.readdir({directory: this.directory, path: this.cachePath});
      dir = res.files;

      this.logger.info('Cache contains files.', dir);

    }catch(e){
      this.logger.info('Mediacache was empty');
    }

    urls = urls.filter(u => !dir.includes(this.hashFilename(u)));

    const obs = from(urls)
      .pipe(
        mergeMap(url => this.http.get(url, {responseType: 'blob'})
            .pipe(map(res => ({url, data: res})))
        ),
        mergeMap((res: DataTransform<Blob>) => from(this.convertFile(res.data))
          .pipe(map(b64 => ({url: res.url, data: b64})))
        ),
        concatMap((res: DataTransform<string>) => this.saveFile(res)),
        toArray()
      );

    return obs.toPromise();
  }

  convertFile(blob: Blob): Promise<string> {
    return new Promise<string>((res, err) => {
      const reader = new FileReader();

      this.logger.log('Converting blob', blob);
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        res(reader.result as string);
      };

      reader.onerror = err;
    });
  }

  saveFile(file: DataTransform<string>) {
    const hash = this.hashFilename(file.url);

    this.logger.info('Caching file ' + file.url);

    return Filesystem.writeFile({
      directory: this.directory,
      path: this.cachePath + '/' + hash,
      recursive: true,
      data: file.data
    });
  }

  hashFilename(name: string) : string {
    const hash = Md5.hashStr(name);
    let extension = name.split('.').pop();

    return hash + '.' + extension;
  }


  async getUri(url: string): Promise<SafeUrl | string> {

    if (this.cachedUris.has(url)){
      let res = this.cachedUris.get(url);
      if(isObservable<SafeUrl>(res)){
        res = await res.toPromise();
      }

      return res;
    }

    const deferObservable = new Subject<SafeUrl | string>();
    this.cachedUris.set(url, deferObservable);

    let cached: string | SafeUrl = url;

    try{

      if(Capacitor.getPlatform() === 'web'){
        const blob = await this.loadMediaBlob(url);
        if(blob == null){
          throw new CapacitorException('Could not load cached file ' + url);
        }

        cached = URL.createObjectURL(blob);

      }else{
        const hash = this.hashFilename(url);
        const src = await Filesystem.getUri({directory: this.directory, path: this.cachePath + '/' + hash});
        cached = Capacitor.convertFileSrc(src.uri); // URL.createObjectURL(blob);
      }

      cached = this.sanitizer.bypassSecurityTrustUrl(cached as string);
      this.cachedUris.set(url, cached);

    }catch (e){
      this.logger.error('Error while loading cached file ' + url,e);
      this.cachedUris.delete(url);
      cached = url;
    }

    deferObservable.next(cached);
    deferObservable.complete();

    return cached;

  }

  async loadMediaBlob(url: string): Promise<Blob> {
    this.logger.log('Loading blob from cache for ' + url);
    try{
      const hash = this.hashFilename(url);
      const b64data = await Filesystem.readFile({directory: this.directory, path: this.cachePath + '/' + hash});

      let mimetype = 'image/jpeg';

      if(url.endsWith('.mp4')) {
        mimetype = 'video/mp4';
      }else if(url.endsWith('png')) {
        mimetype = 'image/png'
      }else if(url.endsWith('bmp')) {
        mimetype = 'image/bmp';
      }



      const resp = await fetch(`data:${mimetype};base64,${b64data.data}`);
      return await resp.blob();

    }catch (e){
      this.logger.error('URL ' + url + ' not found in cache.');
      return null;
    }
  }


}
